import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../stores/useAuthStore';
import useCourseStore from '../stores/useCourseStore';
import CourseCard from '../components/CourseCard';
import DashboardLayout from '../components/DashboardLayout';
import axiosClient from '../services/api';

const MyCourses = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { enrolledCourses, fetchMyCourses, isLoading: isLoadingPersonal, error: errorPersonal } = useCourseStore();

  const [orgEnrollments, setOrgEnrollments] = useState([]);
  const [schoolEnrollments, setSchoolEnrollments] = useState([]);
  const [loadingOrg, setLoadingOrg] = useState(false);
  const [loadingSchool, setLoadingSchool] = useState(false);
  const [errorOrg, setErrorOrg] = useState(null);
  const [errorSchool, setErrorSchool] = useState(null);

  const fetchOrgEnrollments = async () => {
    setLoadingOrg(true);
    setErrorOrg(null);
    try {
      const res = await axiosClient.get('/courses/org/my_courses');
      if (res.data.success) {
        setOrgEnrollments(res.data.enrollments);
      } else {
        setErrorOrg(res.data.error || 'Failed to load organization courses');
      }
    } catch (err) {
      setErrorOrg(err.response?.data?.error || 'Failed to load organization courses');
    } finally {
      setLoadingOrg(false);
    }
  };

  const fetchSchoolEnrollments = async () => {
    setLoadingSchool(true);
    setErrorSchool(null);
    try {
      const res = await axiosClient.get('/courses/school/my_courses');
      if (res.data.success) {
        setSchoolEnrollments(res.data.enrollments);
      } else {
        setErrorSchool(res.data.error || 'Failed to load school courses');
      }
    } catch (err) {
      setErrorSchool(err.response?.data?.error || 'Failed to load school courses');
    } finally {
      setLoadingSchool(false);
    }
  };

  useEffect(() => {
    fetchMyCourses();
    fetchOrgEnrollments();
    fetchSchoolEnrollments();
  }, [fetchMyCourses]);

  const isLoading = isLoadingPersonal || loadingOrg || loadingSchool;

  const hasAnyCourses = enrolledCourses.length > 0 || orgEnrollments.length > 0 || schoolEnrollments.length > 0;


  if (isLoading) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </DashboardLayout>
    );
  }


  return (
    <DashboardLayout userRole={user.role}>
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">
              {user?.role === 'student' ? 'My Courses' : 'My Teaching'}
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              {user?.role === 'student'
                ? 'Continue your learning journey'
                : 'Manage your courses and track student progress'
              }
            </p>
          </div>

          {(errorPersonal || errorOrg || errorSchool) && (
            <div className="mb-6 p-3 bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 rounded-lg text-sm">
              {errorPersonal && <div>{errorPersonal}</div>}
              {errorOrg && <div>{errorOrg}</div>}
              {errorSchool && <div>{errorSchool}</div>}
            </div>
          )}

           {hasAnyCourses ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Personal Enrollments */}
              {enrolledCourses.map((course) => (
                <CourseCard
                  key={`personal-${course._id}`}
                  course={course}
                  onEnroll={() => navigate(`/course/${course.slug}/${course._id}`)}
                  isEnrolled={true}
                  enrollmentSource="personal"
                />
              ))}

              {/* Organization Enrollments */}
              {orgEnrollments.map((enrollment) => (
                <CourseCard
                  key={`org-${enrollment._id}`}
                  course={enrollment.course}
                  isEnrolled={true}
                  enrollmentSource="org"
                  sourceName={enrollment.companyName}
                />
              ))}

              {/* School Enrollments */}
              {schoolEnrollments.map((enrollment) => (
                <CourseCard
                  key={`school-${enrollment._id}`}
                  course={enrollment.course}
                  isEnrolled={true}
                  enrollmentSource="school"
                  sourceName={enrollment.schoolName}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">
                {user?.role === 'student' ? '🎓' : '👩‍🏫'}
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
                {user?.role === 'student'
                  ? 'No enrolled courses yet'
                  : 'No courses created yet'
                }
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                {user?.role === 'student'
                  ? 'Browse our catalog to find your first course'
                  : 'Create your first course to start teaching'
                }
              </p>
              <button
                onClick={() => user?.role === 'student' ? navigate('/courses') : navigate('/instructor/create-course')}
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 transition-all duration-200"
              >
                {user?.role === 'student' ? 'Browse Courses' : 'Create Course'}
              </button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MyCourses;